import AppComponent from '../base/app-component.ts';
import { apiPost, apiPatch } from '../lib/api-client.ts';
import { getCasseMap } from '../lib/casse-info.ts';

const CAMPI_OPZIONALI = [
  'codiceFiscale', 'partitaIva',
  'indirizzo', 'cap', 'citta', 'provincia',
  'telefono', 'cellulare', 'fax', 'email',
  'dataIscrizione', 'dataCessazione', 'dataSospensione',
  'ccnl', 'codiceConsorzio', 'codicePagamento',
  'ac', 'albo', 'numOperai', 'tipoServizio', 'codce',
] as const;

const TUTTI_I_CAMPI = ['cassa', 'codImp', 'ragioneSociale', ...CAMPI_OPZIONALI] as const;

/**
 * wc-edit-impresa — Inserimento e modifica impresa.
 *
 * Attributi:
 *   impresa-id  (opzionale) ID impresa da modificare; assente = inserimento
 *   mode        (opzionale) "manuale" | "cassa" (default).
 *                  - cassa: cassa+codImp obbligatori, POST/PUT su /api/imprese
 *                  - manuale: cassa/codImp nascosti+generati, POST /manuale + PATCH /:id
 *
 * Emette:
 *   impresa-salvata  { detail: { impresa, isNew } }
 */
export class EditImpresa extends AppComponent {
  static override componentName = 'wc-edit-impresa';
  static override observedAttributes = ['mode'];

  protected override get templateId() { return 'edit-impresa'; }

  private editImpresaId = 0;

  private get isManuale(): boolean {
    return this.attributeString('mode') === 'manuale';
  }

  protected override onAttributeChanged(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'mode' && oldValue !== newValue && this.isConnected) {
      this.render();
    }
  }

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      casse: Object.values(getCasseMap()),
      isManuale: this.isManuale,
    };
  }

  protected override afterRender(): void {
    const impresaId = this.attributeNumber('impresa-id');
    if (impresaId) {
      this.editImpresaId = impresaId;
      this.loadImpresa(impresaId);
      this.setTitleModifica();
    }
    this.setupForm();
  }

  /** Reset completo per riapertura in modalità "nuovo" */
  reset(): void {
    this.editImpresaId = 0;
    this.clearForm();
    this.setTitleNuovo();
  }

  /** Carica un'impresa esistente per la modifica */
  edit(id: number): void {
    this.editImpresaId = id;
    this.clearForm();
    this.loadImpresa(id);
    this.setTitleModifica();
  }

  private clearForm(): void {
    for (const el of this.$$('input')) {
      (el as HTMLInputElement).value = '';
    }
    for (const el of this.$$('select[data-field]')) {
      (el as HTMLSelectElement).selectedIndex = 0;
    }
    this.nascondiValidationError();
  }

  private setupForm(): void {
    const form = this.$('[data-form="edit-impresa"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }
  }

  private async loadImpresa(id: number): Promise<void> {
    const result = await this.api.imprese.dettaglio(id);
    if (!result.ok) {
      this.mostraValidationError(result.error);
      return;
    }
    this.populateForm(result.data as unknown as Record<string, unknown>);
  }

  private async handleSubmit(): Promise<void> {
    const val = (sel: string) =>
      (this.$(`[data-field="${sel}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ?? '';

    const ragioneSociale = val('ragioneSociale');
    const codiceFiscale = val('codiceFiscale');
    const partitaIva = val('partitaIva');

    if (this.isManuale) {
      if (!ragioneSociale && !codiceFiscale && !partitaIva) {
        this.mostraValidationError('Almeno uno tra Ragione sociale, Codice fiscale o Partita IVA e\' obbligatorio');
        return;
      }
    } else {
      const cassa = val('cassa');
      const codImp = val('codImp');
      if (!cassa || !codImp || !ragioneSociale) {
        this.mostraValidationError('Cassa, codice impresa e ragione sociale sono obbligatori');
        return;
      }
    }

    const dati: Record<string, string> = {};
    if (ragioneSociale) dati.ragioneSociale = ragioneSociale;
    if (!this.isManuale) {
      dati.cassa = val('cassa');
      dati.codImp = val('codImp');
    }
    for (const campo of CAMPI_OPZIONALI) {
      const v = val(campo);
      if (v) dati[campo] = v;
    }

    if (this.isManuale) {
      await this.submitManuale(dati);
      return;
    }

    if (this.editImpresaId > 0) {
      const result = await this.api.imprese.aggiorna(this.editImpresaId, dati);
      if (!result.ok) {
        this.mostraValidationError(result.error);
        return;
      }
      Weext.toast('Impresa aggiornata', { type: 'success' });
      this.dispatch('impresa-salvata', { impresa: result.data, isNew: false });
    } else {
      const result = await this.api.imprese.crea(dati);
      if (!result.ok) {
        this.mostraValidationError(result.error);
        return;
      }
      Weext.toast('Impresa inserita', { type: 'success' });
      this.dispatch('impresa-salvata', { impresa: result.data, isNew: true });
    }
  }

  private async submitManuale(dati: Record<string, string>): Promise<void> {
    const isNew = this.editImpresaId === 0;
    const result = isNew
      ? await apiPost<Record<string, unknown>>('imprese/manuale', dati)
      : await apiPatch<Record<string, unknown>>(`imprese/${this.editImpresaId}`, dati);
    if (!result.ok) {
      this.mostraValidationError(result.error);
      return;
    }
    Weext.toast(isNew ? 'Impresa inserita' : 'Impresa aggiornata', { type: 'success' });
    this.dispatch('impresa-salvata', { impresa: result.data, isNew });
    const redirect = this.attributeString('data-redirect-on-save');
    if (redirect) {
      window.location.href = redirect;
    }
  }

  private populateForm(imp: Record<string, unknown>): void {
    for (const field of TUTTI_I_CAMPI) {
      const el = this.$(`[data-field="${field}"]`) as HTMLInputElement | HTMLSelectElement | null;
      if (el) el.value = (imp[field] as string) ?? '';
    }
  }

  private mostraValidationError(messaggio: string): void {
    const el = this.$('[data-validation-error]');
    if (!el) return;
    el.textContent = messaggio;
    el.classList.remove('d-none');
  }

  private nascondiValidationError(): void {
    const el = this.$('[data-validation-error]');
    if (!el) return;
    el.textContent = '';
    el.classList.add('d-none');
  }

  private setTitleNuovo(): void {
    const title = this.$('[data-modal-title]');
    if (!title) return;
    title.innerHTML = this.isManuale
      ? '<i class="bi bi-building-add me-2"></i>Nuova impresa (manuale)'
      : '<i class="bi bi-building-add me-2"></i>Nuova impresa';
  }

  private setTitleModifica(): void {
    const title = this.$('[data-modal-title]');
    if (!title) return;
    title.innerHTML = this.isManuale
      ? '<i class="bi bi-pencil me-2"></i>Modifica impresa (manuale)'
      : '<i class="bi bi-pencil me-2"></i>Modifica impresa';
  }
}
